import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Autocomplete,
  Box,
  IconButton,
  Grid,
  FormControl,
  FormLabel,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";
import { postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#D5D7DA",
      borderRadius: "10px",
    },
  },
}));

const LeaveRequestForm = ({ handleClose }) => {
  const [formValues, setFormValues] = useState({
    studentName: "",
    studentEmail: "",
    assignedNilai: "",
    parentName: "",
    parentEmail: "",
    leaveFrom: "",
    leaveTo: "",
    days: "",
    reason: "",
    description: "",
    declaration: false,
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  // Auto-calculate days when leaveFrom or leaveTo changes
  useEffect(() => {
    if (formValues.leaveFrom && formValues.leaveTo) {
      const from = new Date(formValues.leaveFrom);
      const to = new Date(formValues.leaveTo);
      const diff = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
      if (diff > 0) {
        setFormValues((prev) => ({ ...prev, days: String(diff) }));
      } else {
        setFormValues((prev) => ({ ...prev, days: "" }));
      }
    }
  }, [formValues.leaveFrom, formValues.leaveTo]);

  const handleBlur = (field) => {
    if (field !== "declaration" && !formValues[field]) {
      setErrors((prev) => ({ ...prev, [field]: "This field is required." }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["studentName", "studentEmail", "leaveFrom", "leaveTo", "reason", "description"];
    const newErrors = {};
    required.forEach((key) => {
      if (!formValues[key]) newErrors[key] = "This field is required.";
    });
    if (!formValues.declaration) newErrors.declaration = "You must accept the declaration.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      try {
        await postData("student/leave/create", formValues);
        setToast({ open: true, severity: "success", message: "Leave request submitted successfully" });
        handleClose();
      } catch (error) {
        setToast({ open: true, severity: "error", message: "Failed to submit leave request" });
      }
    }
  };

  const formClose = () => {
    handleClose();
    setErrors({});
  };

  const reasonOptions = [
    { label: "Medical", value: "Medical" },
    { label: "Personal", value: "Personal" },
    { label: "Family Function", value: "Family Function" },
  ];

  const styles = {
    formContainer: { padding: "20px", minWidth: "520px" },
    label: { color: "#8F95B2", fontSize: "12px" },
    buttonContainer: { display: "flex", justifyContent: "center", gap: 5, mt: 3, mb: 2 },
    cancelButton: {
      color: "#414651",
      fontSize: "16px",
      textTransform: "none",
      padding: "5px 40px",
      borderRadius: "10px",
      border: "1px solid #D5D7DA",
    },
    submitButton: {
      backgroundColor: "#336599",
      color: "#FFFFFF",
      fontSize: "16px",
      textTransform: "none",
      padding: "5px 40px",
      borderRadius: "10px",
    },
  };

  return (
    <Box component="form" sx={styles.formContainer} onSubmit={handleSubmit}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h4 style={{ margin: 0 }}>Add Leave Request</h4>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Grid container spacing={2} px={3}>
        {/* Student Name */}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Student's Name *</FormLabel>
            <StyledTextField
              size="small"
              name="studentName"
              value={formValues.studentName}
              onChange={handleChange}
              onBlur={() => handleBlur("studentName")}
              error={!!errors.studentName}
              helperText={errors.studentName}
            />
          </FormControl>
        </Grid>
        {/* Student Email */}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Student's Email *</FormLabel>
            <StyledTextField
              size="small"
              type="email"
              name="studentEmail"
              value={formValues.studentEmail}
              onChange={handleChange}
              onBlur={() => handleBlur("studentEmail")}
              error={!!errors.studentEmail}
              helperText={errors.studentEmail}
            />
          </FormControl>
        </Grid>
        {/* Assigned Nilai */}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Assigned Nilai</FormLabel>
            <StyledTextField
              size="small"
              name="assignedNilai"
              placeholder="e.g. Nilai 05"
              value={formValues.assignedNilai}
              onChange={handleChange}
            />
          </FormControl>
        </Grid>
        {/* Parent Name */}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Parent Name</FormLabel>
            <StyledTextField
              size="small"
              name="parentName"
              value={formValues.parentName}
              onChange={handleChange}
            />
          </FormControl>
        </Grid>
        {/* Parent Email */}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Parent Email</FormLabel>
            <StyledTextField
              size="small"
              type="email"
              name="parentEmail"
              value={formValues.parentEmail}
              onChange={handleChange}
            />
          </FormControl>
        </Grid>
        {/* Leave From */}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Leave From *</FormLabel>
            <StyledTextField
              size="small"
              type="date"
              name="leaveFrom"
              value={formValues.leaveFrom}
              onChange={handleChange}
              onBlur={() => handleBlur("leaveFrom")}
              error={!!errors.leaveFrom}
              helperText={errors.leaveFrom}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        {/* Leave To */}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Leave To *</FormLabel>
            <StyledTextField
              size="small"
              type="date"
              name="leaveTo"
              value={formValues.leaveTo}
              onChange={handleChange}
              onBlur={() => handleBlur("leaveTo")}
              error={!!errors.leaveTo}
              helperText={errors.leaveTo}
              InputLabelProps={{ shrink: true }}
            />
          </FormControl>
        </Grid>
        {/* Days (auto-calculated) */}
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Days</FormLabel>
            <StyledTextField
              size="small"
              type="number"
              name="days"
              value={formValues.days}
              onChange={handleChange}
              placeholder="Auto-calculated"
              inputProps={{ min: 1 }}
            />
          </FormControl>
        </Grid>
        {/* Reason */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              size="small"
              options={reasonOptions}
              getOptionLabel={(option) => option.label}
              value={formValues.reason || null}
              onChange={(event, newValue) =>
                handleChange({ target: { name: "reason", value: newValue } })
              }
              onBlur={() => handleBlur("reason")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Reason *"
                  error={!!errors.reason}
                  helperText={errors.reason}
                />
              )}
            />
          </FormControl>
        </Grid>
        {/* Description */}
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={styles.label}>Description *</FormLabel>
            <StyledTextField
              size="small"
              name="description"
              multiline
              rows={4}
              value={formValues.description}
              onChange={handleChange}
              onBlur={() => handleBlur("description")}
              error={!!errors.description}
              helperText={errors.description}
            />
          </FormControl>
        </Grid>
        {/* Declaration checkbox */}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="declaration"
                checked={formValues.declaration}
                onChange={handleChange}
                sx={{ color: errors.declaration ? "#f44336" : undefined }}
              />
            }
            label={
              <Typography sx={{ fontSize: "13px", color: errors.declaration ? "#f44336" : "#374151" }}>
                <span
                  style={{ textDecoration: "underline", cursor: "pointer", color: "#244C77" }}
                  onClick={(e) => e.preventDefault()}
                >
                  Declaration of Agreement to Abide by the Absence Policy.
                </span>
              </Typography>
            }
          />
          {errors.declaration && (
            <Typography sx={{ fontSize: "11px", color: "#f44336", ml: 4, mt: -0.5 }}>
              {errors.declaration}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Box sx={styles.buttonContainer}>
        <Button onClick={formClose} sx={styles.cancelButton}>Cancel</Button>
        <Button type="submit" sx={styles.submitButton}>Submit</Button>
      </Box>
    </Box>
  );
};

export default LeaveRequestForm;
